import {
  FormElementBase,
  FormElementType,
  HeadingFormElement,
  MailingAddressAnswerValue,
  FormElementRuleCondition,
  RuleConditionBase,
  RuleConditionType,
  WorkflowStatusRuleCondition,
  FormStatusRuleCondition,
  RepeaterHostAnswerValue,
  ComplianceItemFormElement,
  LoginAccountRuleCondition,
  TimeFrameRuleCondition,
} from "~/api";

/**
 * Type-guard function to check that a `Partial<MailingAddress>`
 * has all keys defined, and is thus a `MailingAddress`
 */
export function isFullMailingAddress(
  val: Partial<MailingAddressAnswerValue>,
): val is MailingAddressAnswerValue {
  const requiredKeys: (keyof MailingAddressAnswerValue)[] = [
    "province",
    "city",
    "postalCode",
    "streetAddress",
  ];
  return requiredKeys.every((key) => key in val);
}

/**
 * Type-guard function to check that a `Partial<RepeaterHostAnswerValue>`
 * has all repeatableGroupGuids, and is thus a `RepeaterHostAnswerValue`
 */
// export function isRepeaterAnswerValue(
//   val: Partial<RepeaterHostAnswerValue>,
// ): val is RepeaterHostAnswerValue {
//   return val.repeatableGroupGuids !== undefined;
// }

export function isRepeaterAnswerValue(
  value: unknown,
): value is RepeaterHostAnswerValue {
  return (
    typeof value === "object" &&
    value !== null &&
    "repeatableGroupGuids" in value &&
    Array.isArray((value as any).repeatableGroupGuids)
  );
}

/**
 * Type-guard function to check that a `FormElementBase`
 * is specifically a `ComplianceItemFormElement`
 */
export function isComplianceFormElement(
  val: ComplianceItemFormElement,
): val is ComplianceItemFormElement {
  return val.type === FormElementType.COMPLIANCE_ITEM;
}

/**
 * Type-guard function to check that a `FormElementBase`
 * is specifically a `HeadingFormElement`
 */
export function isHeadingFormElement(
  val: FormElementBase,
): val is HeadingFormElement {
  return val.type === FormElementType.HEADING;
}

/**
 * check if the condition is a form element based condition
 * @param condition the condition to check
 */
export function isFormElementBasedCondition(
  condition: RuleConditionBase,
): condition is FormElementRuleCondition {
  return condition.conditionType === RuleConditionType.FORM_ELEMENT_BASED;
}

/**
 * check if the condition is a workflow status based condition
 * @param condition the condition to check
 */
export function isWorkflowStatusBasedCondition(
  condition: RuleConditionBase,
): condition is WorkflowStatusRuleCondition {
  return condition.conditionType === RuleConditionType.WORKFLOW_STATUS_BASED;
}

/**
 * check if the condition is a form status based condition
 * @param condition the condition to check
 */
export function isFormStatusBasedCondition(
  condition: RuleConditionBase,
): condition is FormStatusRuleCondition {
  return condition.conditionType === RuleConditionType.FORM_STATUS_BASED;
}

/**
 * check if the condition is a login account based condition
 * @param condition the condition to check
 */
export function isLoginAccountBasedCondition(
  condition: RuleConditionBase,
): condition is LoginAccountRuleCondition {
  return condition.conditionType === RuleConditionType.LOGIN_ACCOUNT_BASED;
}

/**
 * check if the condition is a login account based condition
 * @param condition the condition to check
 */
export function isTimeFrameBasedCondition(
  condition: RuleConditionBase,
): condition is TimeFrameRuleCondition {
  return condition.conditionType === RuleConditionType.TIME_FRAME_BASED;
}
